/**
 * Diverging Bar Chart Type Definition
 * Horizontal bars extending left (negative) and right (positive) from a center zero line
 */

import {
  numCategoriesControl,
  barOrderingControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControlsHorizontal,
  legendControls,
  colorControls,
} from '../shared/controls';

import { SINGLE_COLORS } from '../shared/palettes';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  spineCode,
  finishCode,
  sortingCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';
import { getPrimaryColorPair } from '../shared/palettes';

// Sample data: generic categories with positive/negative deltas
function generateSampleData(n) {
  const values = [18.5, 12.3, 8.1, 3.2, -2.4, -5.8, -9.1, -14.3, 5.7, -1.2];
  const items = [];
  for (let i = 0; i < Math.min(n, 10); i++) {
    items.push({ name: `Cat ${String.fromCharCode(65 + i)}`, value: values[i] });
  }
  return items;
}

const divergingBar = {
  id: 'diverging-bar',
  name: 'Diverging Bar Chart',
  category: 'diverging-bar',
  description: 'Positive/negative deltas from a baseline',

  getSampleData: (config) => generateSampleData(config.numCategories || 8),

  sampleData: [
    { name: 'Cat A', value: 18.5 },
    { name: 'Cat B', value: 12.3 },
    { name: 'Cat C', value: 8.1 },
    { name: 'Cat D', value: 3.2 },
    { name: 'Cat E', value: -2.4 },
    { name: 'Cat F', value: -5.8 },
    { name: 'Cat G', value: -9.1 },
    { name: 'Cat H', value: -14.3 },
  ],

  defaultConfig: {
    positiveColor: '#4C6EE6',
    negativeColor: '#C44B3D',
    color: '#4C6EE6',
    useBluesPalette: false,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 8,
    outputFormat: 'matplotlib',
    showGrid: true,
    showValues: true,
    valueDecimals: 1,
    ylabel: 'Category',
    xlabel: 'Value',
    title: '',
    xMin: '',
    xMax: '',
    barOrdering: 'descending',
    barHeight: 0.6,
    centerLineStyle: 'solid',
    centerLineValue: 0,
    showLegend: false,
    legendPosition: 'top',
    colorOverrides: {},
  },

  controls: [
    {
      key: 'positiveColor',
      label: 'Positive Color',
      type: 'color',
      options: SINGLE_COLORS.map(c => c.value),
    },
    {
      key: 'negativeColor',
      label: 'Negative Color',
      type: 'color',
      options: SINGLE_COLORS.map(c => c.value),
    },
    ...colorControls,
    numCategoriesControl,
    barOrderingControl,
    {
      key: 'barHeight',
      label: 'Bar Height',
      type: 'slider',
      min: 0.3,
      max: 0.9,
      step: 0.1,
      unit: '',
    },
    {
      key: 'centerLineStyle',
      label: 'Center Line',
      type: 'select',
      options: [
        { label: 'Solid', value: 'solid' },
        { label: 'Dashed', value: 'dashed' },
        { label: 'None', value: 'none' },
      ],
    },
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    ...legendControls,
    ...axisControlsHorizontal,
  ],

  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value;
    const n = config.numCategories || 8;

    // Compute tight_layout rect that leaves room for the external legend
    function legendRect() {
      if (!config.showLegend) return [0, 0.03, 1, 0.97];
      const pos = config.legendPosition;
      if (pos === 'bottom') return [0, 0.15, 1, 0.97];
      if (pos === 'right')  return [0, 0.03, 0.84, 0.97];
      return [0, 0.03, 1, 0.88]; // top (default)
    }
    const sampleValues = [18.5, 12.3, 8.1, 3.2, -2.4, -5.8, -9.1, -14.3];
    const categories = Array.from({ length: n }, (_, i) => `Cat ${String.fromCharCode(65 + i)}`);
    const values = sampleValues.slice(0, n);

    const colorPair = getPrimaryColorPair(config, s);
    const posColor = colorPair.positive;
    const negColor = colorPair.negative;
    const barHeight = config.barHeight ?? 0.6;
    const centerVal = config.centerLineValue !== '' && config.centerLineValue != null
      ? parseFloat(config.centerLineValue)
      : 0;
    const centerStyle = config.centerLineStyle || 'solid';
    const decimals = config.valueDecimals ?? 1;

    // Seaborn output — matplotlib directly (seaborn doesn't support per-bar conditional coloring)
    if (config.outputFormat === 'seaborn') {
      return `${seabornSetup(s)}

# ======== DATA ========
# Seaborn does not natively support per-bar conditional coloring for diverging bars; using matplotlib directly
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
values = df['${dataContext.mapping.value}'].tolist()` : `categories = ${JSON.stringify(categories)}
values = [${values.join(', ')}]`}
# ======================
${sortingCode(config, 'values', 'categories')}
${seabornCreateFigure(6, 5, 300, s)}

# Diverging bar colors: positive vs negative
colors = ['${posColor}' if v >= ${centerVal} else '${negColor}' for v in values]
bars = ax.barh(categories, values, color=colors, height=${barHeight},
               edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0)

${centerStyle !== 'none' ? `# Center line at ${centerVal}
ax.axvline(x=${centerVal}, color='${s?.axisColor || '#000000'}', linewidth=1.0, linestyle='${centerStyle === 'dashed' ? '--' : '-'}')` : '# Center line disabled'}

${config.showValues ? `# Value labels at bar tips
for bar, val in zip(bars, values):
    w = bar.get_width()
    prefix = '+' if val >= ${centerVal} else ''
    ax.text(w, bar.get_y() + bar.get_height()/2., f'{prefix}{val:.${decimals}f}',
            ha='left' if val >= ${centerVal} else 'right', va='center', fontsize=10, color='${s?.foreground || '#000000'}')` : '# Value labels disabled'}

${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornGridCode(config, 'x', s)}
${config.showLegend ? `# Add legend
from matplotlib.patches import Patch
legend_elements = [Patch(facecolor='${posColor}', label='Positive'), Patch(facecolor='${negColor}', label='Negative')]
ax.legend(handles=legend_elements, loc='${config.legendPosition === 'top' ? 'upper center' : config.legendPosition === 'bottom' ? 'lower center' : 'center left'}',
         bbox_to_anchor=(${config.legendPosition === 'top' ? '0.5, 1.12' : config.legendPosition === 'bottom' ? '0.5, -0.12' : '1.02, 0.5'}), frameon=False, fontsize=${s?.legendSize || 10}, ncol=${config.legendPosition === 'right' ? '1' : '2'})` : '# Legend disabled'}
${seabornSpineCode(s)}
${seabornFinishCode('diverging_bar_chart', legendRect())}`;
    }

    // Matplotlib output (default)
    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
categories = df['${dataContext.mapping.category}'].tolist()
values = df['${dataContext.mapping.value}'].tolist()` : `categories = ${JSON.stringify(categories)}
values = [${values.join(', ')}]`}
# ======================
${sortingCode(config, 'values', 'categories')}
${createFigure(6, 5, 300, s)}

# Diverging bar colors: positive vs negative
colors = ['${posColor}' if v >= ${centerVal} else '${negColor}' for v in values]
bars = ax.barh(categories, values, color=colors, height=${barHeight},
               edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0)

${centerStyle !== 'none' ? `# Center line at ${centerVal}
ax.axvline(x=${centerVal}, color='${s?.axisColor || '#000000'}', linewidth=1.0, linestyle='${centerStyle === 'dashed' ? '--' : '-'}')` : '# Center line disabled'}

${config.showValues ? `# Value labels at bar tips
for bar, val in zip(bars, values):
    w = bar.get_width()
    prefix = '+' if val >= ${centerVal} else ''
    ax.text(w, bar.get_y() + bar.get_height()/2., f'{prefix}{val:.${decimals}f}',
            ha='left' if val >= ${centerVal} else 'right', va='center', fontsize=10, color='${s?.foreground || '#000000'}')` : '# Value labels disabled'}

${titleCode(config, s)}
${axisLabelsCode(config, s)}
${gridCode(config, 'x', s)}
${config.showLegend ? `# Add legend
from matplotlib.patches import Patch
legend_elements = [Patch(facecolor='${posColor}', label='Positive'), Patch(facecolor='${negColor}', label='Negative')]
ax.legend(handles=legend_elements, loc='${config.legendPosition === 'top' ? 'upper center' : config.legendPosition === 'bottom' ? 'lower center' : 'center left'}',
         bbox_to_anchor=(${config.legendPosition === 'top' ? '0.5, 1.12' : config.legendPosition === 'bottom' ? '0.5, -0.12' : '1.02, 0.5'}), frameon=False, fontsize=${s?.legendSize || 10}, ncol=${config.legendPosition === 'right' ? '1' : '2'})` : '# Legend disabled'}
${spineCode(s)}
${finishCode('diverging_bar_chart', legendRect())}`;
  },
};

export default divergingBar;
